import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { EVENT_VIEWS, stripTimeZone, VIEW_TYPES } from '../components/events/components/helpers/events.helper';
import { extractUrlParams } from '../helpers/pagination';
import {
  getEventsUrl,
  getSelectedFilter,
  getSelectedFilterIds,
  getDayObject,
  getMonthRange,
  getDateParams,
  getEventSearchDateParams,
  getCurrentFormattedDate,
} from '../helpers/events-state.helper';

const VIEW_TYPE = Object.freeze({
  DATE: 'DATE',
  MONTH: 'MONTH',
});

const REQ_STATE = Object.freeze({
  INITIAL: 'INITIAL',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
});

export const useEventsStore = defineStore('events', () => {
  const hasSections = ref(false);
  const pageFilters = ref([]);
  const allEvents = ref([]);
  const fetchingEvents = ref(REQ_STATE.INITIAL);
  const sections = ref([]);
  const filters = ref([]);
  const selectedCalendars = ref([]);
  const selectedFilters = ref([]);
  const selectedFilter = ref([]);
  const currentView = ref(EVENT_VIEWS.CALENDAR_VIEW.componentName);
  const listViewType = ref('month');
  const currentDate = ref(new Date().toLocaleDateString('en-US'));
  const calendarViewType = ref(VIEW_TYPES.MONTH);
  const queryParams = ref({});
  const searchText = ref(null);
  const lastFocusedElem = ref(null);
  const lastView = ref(EVENT_VIEWS.CALENDAR_VIEW.componentName);
  const searchTagText = ref(null);
  const selectedYearUpdated = ref(false);
  const lastActiveEndDate = ref(null);
  const totalPastEvents = ref(0);
  const totalUpcomingEvents = ref(0);

  const getPageFilters = computed(() => pageFilters.value);
  const getCurrentFullDate = computed(() => new Date(currentDate.value));
  const getCurrentDate = computed(() => new Date(currentDate.value));
  const getCurrentDay = computed(() => {
    const date = new Date(currentDate.value);
    const day = date.getDate();
    return isNaN(day) ? new Date().getDate() : day;
  });
  const getCurrentMonth = computed(() => {
    const date = new Date(currentDate.value);
    const month = date.getMonth();
    return isNaN(month) ? new Date().getMonth() : month;
  });
  const getCurrentYear = computed(() => {
    const date = new Date(currentDate.value);
    const year = date.getFullYear();
    return isNaN(year) ? new Date().getFullYear() : year;
  });
  const isLoading = computed(() => [REQ_STATE.INITIAL, REQ_STATE.PENDING].includes(fetchingEvents.value));

  function setHasSections(value) {
    hasSections.value = value;
  }

  function setPageFilters(value) {
    pageFilters.value = value || [];
  }

  function changeEventView(viewName) {
    if (!EVENT_VIEWS[viewName]) {
      console.error(`${viewName} is not a valid Event View Name`);
      return;
    }
    currentView.value = EVENT_VIEWS[viewName].componentName;
  }

  function setAllEvents(events) {
    allEvents.value = events;
  }

  function setReqStatus(reqStatus) {
    if (!Object.values(REQ_STATE).includes(reqStatus)) {
      console.error('Invalid Request Status: ', reqStatus);
    }
    fetchingEvents.value = reqStatus;
  }

  function setFilters(value) {
    filters.value = value;
  }

  function setSections(value) {
    sections.value = value;
  }

  function setSelectedFilters({ checked, value }) {
    if (checked) {
      selectedFilters.value = [...selectedFilters.value, value];
      return;
    }
    selectedFilters.value = selectedFilters.value.filter((id) => id !== value);
  }

  function setSelectedCalendars({ checked, value }) {
    if (checked) {
      selectedCalendars.value = [...selectedCalendars.value, value];
      return;
    }
    selectedCalendars.value = selectedCalendars.value.filter((id) => id !== value);
  }

  function initSelectedCalendars(selectedCalendarsIds) {
    selectedCalendars.value = selectedCalendarsIds.map((val) => parseInt(val));
  }

  function initSelectedFilters(selectedFiltersIds) {
    selectedFilters.value = selectedFiltersIds.map((val) => parseInt(val));
  }

  function setCurrentDate(date) {
    const currentDateObj = new Date(Date.parse(stripTimeZone(currentDate.value)));
    const { year, month, day } = {
      year: date.year !== undefined ? date.year : currentDateObj.getFullYear(),
      month: date.month !== undefined ? date.month : currentDateObj.getMonth(),
      day: date.day !== undefined ? date.day : 1,
    };
    currentDate.value = new Date(year, month, day).toLocaleDateString('en-US');
  }

  function setSelectedYearUpdated(value) {
    selectedYearUpdated.value = value;
  }

  function setCalViewType(view = VIEW_TYPE.MONTH) {
    calendarViewType.value = view;
  }

  function setListViewType(view) {
    listViewType.value = view;
  }

  function setQueryParams(newQueryParams) {
    queryParams.value = { ...queryParams.value, ...newQueryParams };
    Object.entries(newQueryParams).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        delete queryParams.value[key];
      }
    });
  }

  function setSearchText(value) {
    searchText.value = value;
  }

  function setLastFocusedElem(payload) {
    lastFocusedElem.value = payload;
  }

  function setLastView(payload) {
    lastView.value = payload;
  }

  function setEventSearchTagText(text) {
    searchTagText.value = text;
  }

  function setLastActiveEndDate(date) {
    lastActiveEndDate.value = date;
  }

  function setTotalPastEvents(total) {
    totalPastEvents.value = total;
  }

  function setTotalUpcomingEvents(total) {
    totalUpcomingEvents.value = total;
  }

  async function updateDateFilters({ year, month, day, singleDay, target }) {
    setCurrentDate({ year, month, day });
    setLastFocusedElem(target);
    let dateParams;
    if (singleDay) {
      dateParams = getDayObject(
        year || getCurrentYear.value,
        month || getCurrentMonth.value,
        day || getCurrentDay.value,
      );
    } else {
      dateParams = getMonthRange(year || getCurrentYear.value, month || getCurrentMonth.value);
    }
    setQueryParams(dateParams);
  }

  async function updateSearchText(search) {
    setLastView(currentView.value === 'EventsCalendarView' ? 'CALENDAR_VIEW' : 'LIST_VIEW');
    setSearchText(search);
    setQueryParams({ search });
    if (search) {
      changeEventView('SEARCH_VIEW');
    } else {
      changeEventView(lastView.value);
    }
  }

  async function updateCalendars({ checked, value, target }) {
    setLastFocusedElem(target);
    setSelectedCalendars({ checked, value });
    setQueryParams({ section_ids: selectedCalendars.value.toString() });
  }

  async function updateFilters(filtersArg) {
    setLastFocusedElem(filtersArg.target);
    setSelectedFilters(filtersArg);
    setQueryParams({ filter_ids: selectedFilters.value.toString() });
  }

  async function filterView(view) {
    const { useMainStore } = await import('./mainStore.js');
    const mainStore = useMainStore();
    let viewType = view;
    if (!viewType) {
      viewType = searchText.value ? 'search-view' : get(mainStore.globals, 'settings.eventsPage.view_type');
    }
    switch (viewType) {
      case 'list-month':
        changeEventView('LIST_VIEW');
        setListViewType('month');
        break;
      case 'list-upcoming':
      case 'list-all':
        changeEventView('LIST_VIEW');
        setListViewType('all');
        break;
      case 'cal-month':
        changeEventView('CALENDAR_VIEW');
        setCalViewType('dayGridMonth');
        break;
      case 'cal-week':
        changeEventView('CALENDAR_VIEW');
        setCalViewType('timeGridWeek');
        break;
      case 'search-view':
        changeEventView('SEARCH_VIEW');
        break;
      default:
        changeEventView('LIST_VIEW');
        setListViewType('month');
    }
  }

  async function setPastEvents({ params, query, useQueryCalendars, url, $axios }) {
    const { useTranslationStore } = await import('./translationStore.js');
    const translationStore = useTranslationStore();
    const pastEvents = await $axios.get(url, {
      params: {
        ...params,
        ...getEventSearchDateParams(),
        ...query,
        ...getCurrentFormattedDate(),
        page_no: 1,
        section_ids: !useQueryCalendars ? sections.value.map((i) => i.id).join(',') : query.section_ids,
        paginate: true,
        locale: translationStore.getLocale,
      },
    });
    setTotalPastEvents(pastEvents.data.meta.links.total_entries);
    return pastEvents;
  }

  async function setUpcomingEvents({ params, query, useQueryCalendars, url, $axios }) {
    const { useTranslationStore } = await import('./translationStore.js');
    const translationStore = useTranslationStore();
    const upcomingEvents = await $axios.get(url, {
      params: {
        ...params,
        ...getEventSearchDateParams(0),
        ...query,
        page_no: 1,
        section_ids: !useQueryCalendars ? sections.value.map((i) => i.id).join(',') : query.section_ids,
        paginate: true,
        locale: translationStore.getLocale,
      },
    });
    setTotalUpcomingEvents(upcomingEvents.data.meta.links.total_entries);
    return upcomingEvents;
  }

  async function returnSingleEvent({ url, query, $axios }) {
    const { useTranslationStore } = await import('./translationStore.js');
    const translationStore = useTranslationStore();
    const responseSingleEvent = await $axios.get(`${url}/${query.id}`, {
      params: { locale: translationStore.getLocale },
    });
    const { event } = responseSingleEvent.data;
    const startDate = new Date(event.start_at.replace(/-/g, '/').replace(/T.+/, ''));
    setEventSearchTagText(event.title);
    changeEventView('LIST_VIEW');
    await updateDateFilters({
      year: event.year,
      month: startDate.getMonth(),
      day: startDate.getDate(),
      singleDay: true,
    });
    return [event];
  }

  async function fetchEvents({ query, params, dateParams, useQueryCalendars, url, $axios }) {
    const { useTranslationStore } = await import('./translationStore.js');
    const translationStore = useTranslationStore();
    const refinedQueryParams = {};
    let response = [];
    Object.keys(query).forEach((key) => {
      if (!isEmpty(query[key])) {
        refinedQueryParams[key] = query[key];
      }
    });
    try {
      if (query.search) {
        const pastEventsResult = await setPastEvents({ query, params, useQueryCalendars, url, $axios });
        const upcomingEventsResult = await setUpcomingEvents({ query, params, useQueryCalendars, url, $axios });
        response = pastEventsResult;
        response.data.events = [...pastEventsResult.data.events, ...upcomingEventsResult.data.events];
      } else if (
        (listViewType.value === 'all' && currentView.value === 'EventsListView') ||
        query.view === 'list-all'
      ) {
        response = await $axios.get(url, {
          params: {
            ...params,
            ...refinedQueryParams,
            page_no: 1,
            section_ids: !useQueryCalendars ? sections.value.map((i) => i.id).join(',') : query.section_ids,
            paginate: true,
            locale: translationStore.getLocale,
          },
          cache: { ignoreCache: false },
        });
      } else {
        response = await $axios.get(url, {
          params: {
            ...params,
            ...dateParams,
            ...refinedQueryParams,
            section_ids: !useQueryCalendars ? sections.value.map((i) => i.id).join(',') : query.section_ids,
            paginate: false,
            locale: translationStore.getLocale,
          },
          cache: { ignoreCache: false },
        });
      }
      return response;
    } catch (ex) {
      console.error(ex);
      setReqStatus(REQ_STATE.ERROR);
      throw createError({
        statusCode: 404,
        message: 'This page could not be found',
      });
    }
  }

  async function buildEventComponentData({ $axios }) {
    const { useMainStore } = await import('./mainStore.js');
    const mainStore = useMainStore();
    setReqStatus(REQ_STATE.PENDING);
    setEventSearchTagText('');
    try {
      const query = useRoute().query;
      const slug = useRoute().params.slug;
      const eventsUrl = getEventsUrl(mainStore.links, slug);
      const { url, params } = extractUrlParams(eventsUrl, $axios.defaults.baseURL);
      const selectedFilterValue = getSelectedFilter(query);
      const { calendarsId, filtersId } = getSelectedFilterIds(query);
      if (calendarsId) {
        initSelectedCalendars(calendarsId);
      }
      if (filtersId) {
        initSelectedFilters(filtersId);
      }
      const useQueryCalendars = !(!query.section_ids && sections.value.length);
      const dateParams = getDateParams(query, getCurrentYear.value, getCurrentMonth.value, (mutationName, payload) => {
        if (mutationName === 'setCurrentDate') setCurrentDate(payload);
      });

      const { data } = await fetchEvents({ query, params, dateParams, useQueryCalendars, url, $axios });
      const {
        meta: { filters: filtersData, sections: sectionsData },
      } = data;
      let { events } = data;
      if (query.id) {
        events = await returnSingleEvent({ url, query, $axios });
      }
      setAllEvents([...events]);
      setPageFilters(selectedFilterValue);
      setFilters(filtersData);
      setSections(sectionsData);
      setHasSections(sectionsData && sectionsData.length > 1);
      setQueryParams({ ...params, ...dateParams, ...query });
      setReqStatus(REQ_STATE.SUCCESS);
    } catch (ex) {
      console.error(ex);
      setReqStatus(REQ_STATE.ERROR);
      throw createError({
        statusCode: 404,
        message: 'This page could not be found',
      });
    }
  }

  return {
    hasSections,
    pageFilters,
    allEvents,
    fetchingEvents,
    sections,
    filters,
    selectedCalendars,
    selectedFilters,
    selectedFilter,
    currentView,
    listViewType,
    currentDate,
    calendarViewType,
    queryParams,
    searchText,
    lastFocusedElem,
    lastView,
    searchTagText,
    selectedYearUpdated,
    lastActiveEndDate,
    totalPastEvents,
    totalUpcomingEvents,
    getPageFilters,
    getCurrentFullDate,
    getCurrentDate,
    getCurrentDay,
    getCurrentMonth,
    getCurrentYear,
    isLoading,
    setHasSections,
    setPageFilters,
    changeEventView,
    setAllEvents,
    setReqStatus,
    setFilters,
    setSections,
    setSelectedFilters,
    setSelectedCalendars,
    initSelectedCalendars,
    initSelectedFilters,
    setCurrentDate,
    setSelectedYearUpdated,
    setCalViewType,
    setListViewType,
    setQueryParams,
    setSearchText,
    setLastFocusedElem,
    setLastView,
    setEventSearchTagText,
    setLastActiveEndDate,
    setTotalPastEvents,
    setTotalUpcomingEvents,
    updateDateFilters,
    updateSearchText,
    updateCalendars,
    updateFilters,
    filterView,
    setPastEvents,
    setUpcomingEvents,
    returnSingleEvent,
    fetchEvents,
    buildEventComponentData,
  };
});
